import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';

import { ModalHOC } from 'common/HOCs/ModalHOC';
import { NoItemsPanel } from 'common/components';
import { taggedStylesToString, withTheme } from 'common/styling/theme';
import { change } from 'redux-form';

// import { BasketIcon } from 'common/icons';
import FormFieldsComposer from '../FormFieldsComposer';

import { getDynamicStyles, staticStyles } from './style';

class SymbolGenerateArrayField extends Component {
  // 深拷贝函数，不会序列化函数
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    if (obj instanceof Array) {
      return obj.map(item => SymbolGenerateArrayField.deepClone(item));
    }
    if (typeof obj === 'object') {
      const clonedObj = {};
      Object.keys(obj).forEach(key => {
        clonedObj[key] = SymbolGenerateArrayField.deepClone(obj[key]);
      });
      return clonedObj;
    }
  }

  // 动态创建包含condition字段的schema
  static createDynamicSchema(originalSchema) {
    if (!originalSchema || !originalSchema.items) {
      return originalSchema;
    }
    // 创建schema的深拷贝
    const dynamicSchema = SymbolGenerateArrayField.deepClone(originalSchema);
    // 确保fields对象存在
    if (!dynamicSchema.items.properties) {
      dynamicSchema.items.properties = {};
    }

    // 设置name字段为只读
    if (dynamicSchema.items.properties.name) {
      dynamicSchema.items.properties.name.disabled = true;
    }
    return dynamicSchema;
  }

  // 保留所有字段，包括name字段（已设为只读）
  static filterSchemaFields(schema) {
    if (!schema || !schema.items || !schema.items.fields) return schema;
    // 创建schema的深拷贝
    const filteredSchema = SymbolGenerateArrayField.deepClone(schema);
    // 保留所有字段，不再过滤name字段
    return filteredSchema;
  }

  static isValidSymbol(symbol) {
    return typeof symbol === 'string' && symbol.trim().length > 0;
  }

  static findRewardAmountItem(rewardAmount, symbol) {
    return rewardAmount.find(item => item && String(item.name) === String(symbol)) || {};
  }

  componentDidMount() {
    // 页面初始化时获取所有值
    this.handleNextButtonClick(this.props.index);
  }

  componentDidUpdate(prevProps) {
    // 当formData发生变化时，重新更新数据
    const { formData, index, dispatch, formName } = this.props;

    // 先清理数据结构，确保 percentage/spread 类型下 value 只包含 item 和 reward_amount
    const currentType = formData?.regular_account?.symbol_exceptions?.[index]?.data?.type;
    if (currentType && currentType !== 'fix') {
      const originalValue = formData?.regular_account?.symbol_exceptions?.[index]?.data?.value || {};
      const cleanedValue = {
        item: originalValue.item || [],
        reward_amount: originalValue.reward_amount || [],
      };
      // 检查是否有多余字段需要清理（如 value 字段）
      const hasExtraFields = Object.keys(originalValue).some(key => !['item', 'reward_amount'].includes(key));
      if (hasExtraFields) {
        dispatch(change(formName, `regular_account.symbol_exceptions[${index}].data.value`, cleanedValue));
      }
    }

    // 检查是否真的需要更新fields数据
    // 只有当selectedGroups或reward_amount发生变化时才更新
    const prevSelectedGroups = prevProps.formData?.regular_account?.symbol_exceptions?.[index]?.data?.value?.item || [];
    const currentSelectedGroups = formData?.regular_account?.symbol_exceptions?.[index]?.data?.value?.item || [];

    const prevRewardAmount =
      prevProps.formData?.regular_account?.symbol_exceptions?.[index]?.data?.value?.reward_amount || [];
    const currentRewardAmount = formData?.regular_account?.symbol_exceptions?.[index]?.data?.value?.reward_amount || [];
    const prevRewardNames = prevRewardAmount.map(item => item?.name).filter(Boolean);
    const currentRewardNames = currentRewardAmount.map(item => item?.name).filter(Boolean);

    // 只有当关键数据发生变化时才更新fields
    if (
      JSON.stringify(prevSelectedGroups) !== JSON.stringify(currentSelectedGroups) ||
      JSON.stringify(prevRewardNames) !== JSON.stringify(currentRewardNames)
    ) {
      this.handleNextButtonClick(index);
    }
  }

  onDragEnd = result => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    const [removed] = fields.getAll().splice(startIndex, 1);

    fields.splice(endIndex, 0, removed);
  };

  // Next按钮
  handleNextButtonClick = index => {
    // 如果传入了index参数，使用传入的值；否则使用props中的index（如果有的话），如果都没有则使用默认值0
    const targetIndex = index !== undefined ? index : this.props.index !== undefined ? this.props.index : 0;
    const { fields, formData, dispatch, formName } = this.props;
    // 尝试多种可能的路径获取选中的group items
    let selectedGroups = [];
    // 主路径：从regular_account.symbol_exceptions获取
    if (formData?.regular_account?.symbol_exceptions && formData.regular_account.symbol_exceptions.length > 0) {
      const firstException = formData.regular_account.symbol_exceptions[targetIndex];
      selectedGroups = firstException?.data?.value?.item || [];
    }
    const rewardAmount = formData?.regular_account?.symbol_exceptions?.[targetIndex]?.data?.value?.reward_amount;
    const rewardAmountItems = Array.isArray(rewardAmount) ? rewardAmount : [];

    // 清理 percentage/spread 类型结构：value 仅保留 item 与 reward_amount
    const currentType = formData?.regular_account?.symbol_exceptions?.[targetIndex]?.data?.type;
    if (currentType && currentType !== 'fix') {
      const originalValue = formData?.regular_account?.symbol_exceptions?.[targetIndex]?.data?.value || {};
      const cleanedValue = {
        item: originalValue.item || [],
        reward_amount: originalValue.reward_amount || [],
      };
      if (JSON.stringify(originalValue) !== JSON.stringify(cleanedValue)) {
        dispatch(change(formName, `regular_account.symbol_exceptions[${targetIndex}].data.value`, cleanedValue));
      }
      // 同步本地 selectedGroups
      selectedGroups = cleanedValue.item;
    }
    selectedGroups = Array.isArray(selectedGroups) ? selectedGroups : [];
    // 清空现有的所有字段，重新生成数据
    const currentFields = fields.getAll() || [];
    // 从后往前删除，避免索引变化问题
    for (let i = currentFields.length - 1; i >= 0; i -= 1) {
      fields.remove(i);
    }
    // 为每个选中的group重新创建奖励条目
    selectedGroups.forEach(group => {
      if (SymbolGenerateArrayField.isValidSymbol(group)) {
        const newItem = {
          name: group,
        };
        const existingValue = SymbolGenerateArrayField.findRewardAmountItem(rewardAmountItems, group).value;
        newItem.value = existingValue !== undefined ? existingValue : null;
        fields.push(newItem);
      }
    });
  };

  render() {
    const {
      schema,
      fields,
      meta: { error, pristine },
      formName,
      arraysName,
      submitting,
      theme,
      // index,
    } = this.props;

    const style = taggedStylesToString(staticStyles);
    const dynamicStyles = Object.keys(theme).length ? taggedStylesToString(getDynamicStyles(theme)) : ` `;

    const formError = !pristine && error;
    const isDragDisabled = !schema.ordering || fields.length === 1;
    const isFormError = typeof error === 'object';

    // 创建动态schema，包含condition字段
    const dynamicSchema = SymbolGenerateArrayField.createDynamicSchema(schema);

    // 过滤schema，移除name字段
    const filteredSchema = SymbolGenerateArrayField.filterSchemaFields(dynamicSchema);
    return (
      <div className="FieldArray" id={arraysName}>
        <div className="FieldArray__top-line">
          <div className="FieldArray__title">
            <FormattedMessage id={schema.title}>{txt => <h2>{txt}</h2>}</FormattedMessage>
          </div>
        </div>

        {fields.length ? (
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Droppable droppableId="droppable" isDropDisabled={isDragDisabled}>
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={classNames('FieldArray__isDragging-inner', { isDraggingOver: snapshot.isDraggingOver })}>
                  {fields.map((item, index) => (
                    <Draggable
                      key={`${index}_${fields.length}`}
                      draggableId={item}
                      index={index}
                      isDragDisabled={isDragDisabled}>
                      {(provided, snapshot) => (
                        <div
                          className={classNames('FieldArray__item-inner', {
                            FieldArray__notDraggable: !schema.ordering,
                            FieldArray__isDraggable: snapshot.isDragging,
                            FieldArray__item_error: isFormError && error[index],
                          })}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={provided.draggableProps.style}>
                          <div className="FieldArray__item">
                            <p className="FieldArray__item-number">{index + 1}</p>
                            <FormFieldsComposer
                              name={`${item}`}
                              schema={filteredSchema.items}
                              formName={formName}
                              submitting={submitting}
                            />
                            {isFormError && error[index] && (
                              <FormattedMessage id={error[index]}>
                                {txt => <span className="ArrayField__item-error">{txt}</span>}
                              </FormattedMessage>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <NoItemsPanel
            // captionSlug={schema.title}
            // descriptionSlug={schema.description}
            className="FieldArray__no-items"
            isError={!!formError}
          />
        )}

        <style>{style}</style>
        <style>{dynamicStyles}</style>
      </div>
    );
  }
}

SymbolGenerateArrayField.propTypes = {
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  showModal: PropTypes.func.isRequired,
  meta: PropTypes.object.isRequired,
  fields: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  arraysName: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  theme: PropTypes.object,
  formData: PropTypes.object,
  index: PropTypes.number,
};

SymbolGenerateArrayField.defaultProps = {
  fields: {},
  submitting: false,
  theme: {},
  formData: {},
  index: 0,
};

// 使用connect从Redux store获取表单数据
const mapStateToProps = (state, ownProps) => {
  const formData = state.form && state.form[ownProps.formName] ? state.form[ownProps.formName].values || {} : {};
  return {
    formData,
  };
};

const ThemedArrayField = compose(connect(mapStateToProps), withTheme(), ModalHOC)(SymbolGenerateArrayField);

export default ThemedArrayField;
export { SymbolGenerateArrayField };
